import { Link } from '@tanstack/react-router'
import { Accessibility, Building2, MapPin, Star, Users } from 'lucide-react'

interface MarketplaceRoomSummary {
  name: string
  slug: string
  orgSlug: string
  orgName: string
  image?: string
  location?: string | null
  buildingName?: string | null
  capacity: number
  hourlyRate: number | string | null
  currency?: string
  amenities?: string[]
  accessibilityFeatureCount?: number
  averageRating?: number | null
  reviewCount?: number
}

export function MarketplaceRoomCard({ room }: { room: MarketplaceRoomSummary }) {
  const rate = room.hourlyRate === null ? null : Number(room.hourlyRate)
  const price = rate !== null && Number.isFinite(rate)
    ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: room.currency || 'GBP', maximumFractionDigits: 2 }).format(rate)
    : null

  return (
    <Link
      to="/marketplace/$orgSlug/$roomSlug"
      params={{ orgSlug: room.orgSlug, roomSlug: room.slug }}
      className="group flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border border-border bg-white transition-colors hover:border-gray-400"
    >
      <div className="relative aspect-[20/19] overflow-hidden bg-muted">
        {room.image ? (
          <img src={room.image} alt={room.name} loading="lazy" decoding="async" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center" role="img" aria-label="No room photo available">
            <Building2 className="h-12 w-12 text-gray-400" />
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div>
          <h3 className="line-clamp-2 text-xl font-semibold leading-snug group-hover:underline underline-offset-4">{room.name}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{room.orgName}</p>
          {room.buildingName && room.buildingName !== room.orgName && <p className="mt-1 text-sm text-muted-foreground">{room.buildingName}</p>}
        </div>
        {(room.location || room.buildingName) && (
          <div className="flex items-start gap-1.5 text-sm text-muted-foreground">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="line-clamp-2">{room.location || room.buildingName}</span>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="flex items-center gap-1.5 text-muted-foreground"><Users className="h-4 w-4" />Up to {room.capacity}</span>
          {room.averageRating != null && (room.reviewCount || 0) > 0 && (
            <span className="flex items-center gap-1" aria-label={`${room.averageRating.toFixed(1)} out of 5 from ${room.reviewCount} reviews`}>
              <Star className="h-4 w-4 fill-current" />
              <span className="font-semibold">{room.averageRating.toFixed(1)}</span>
              <span className="text-muted-foreground">({room.reviewCount})</span>
            </span>
          )}
        </div>
        {!!room.amenities?.length && (
          <p className="line-clamp-2 text-sm text-muted-foreground">{room.amenities.slice(0, 3).join(', ')}</p>
        )}
        {!!room.accessibilityFeatureCount && (
          <span className="flex items-center gap-1.5 text-xs font-medium text-community-strong">
            <Accessibility className="h-4 w-4 shrink-0" />
            {room.accessibilityFeatureCount} accessibility {room.accessibilityFeatureCount === 1 ? 'feature' : 'features'}
          </span>
        )}
        <p className="mt-auto border-t border-border pt-3">
          <span className="text-lg font-semibold">{price || 'Price on request'}</span>
          {price && <span className="text-sm text-muted-foreground"> / hour</span>}
        </p>
      </div>
    </Link>
  )
}